import { FC, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useApi } from 'api/ApiProvider';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import type { TaxRate } from 'axiosApi/models';
import type { CreateTaxRateCommand } from 'axiosApi/models/create-tax-rate-command';
import type { UpdateTaxRateCommand } from 'axiosApi/models/update-tax-rate-command';

const AppliesTo = {
  ALL: 0,
  ITEM: 1,
  INVOICE: 2,
} as const;

type TaxFormData = {
  name: string;
  idCountry: number;
  rate: number;
  amount: number;
  appliesTo: number;
};

type TaxFormDrawerProps = {
  open: boolean;
  taxRate: TaxRate | null;
  onClose: () => void;
  onSuccess: () => void;
};

const TaxFormDrawer: FC<TaxFormDrawerProps> = ({ open, taxRate, onClose, onSuccess }) => {
  const api = useApi();
  const toast = useToastMessageQueue();
  const queryClient = useQueryClient();
  const isEditMode = !!taxRate;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TaxFormData>({
    defaultValues: {
      name: '',
      idCountry: 0,
      rate: 0,
      amount: 0,
      appliesTo: AppliesTo.ALL,
    },
  });

  // Fetch countries for dropdown
  const { data: countriesData } = useQuery({
    queryKey: ['countries'],
    queryFn: async () => {
      const response = await api.addressApi.apiVversionListCountriesGet('1');
      return response.data;
    },
  });

  // Reset form when drawer opens/closes or taxRate changes
  useEffect(() => {
    if (open && taxRate) {
      reset({
        name: taxRate.name || '',
        idCountry: taxRate.idCountry || 0,
        rate: taxRate.rate || 0,
        amount: taxRate.amount || 0,
        appliesTo: taxRate.appliesTo ?? AppliesTo.ALL,
      });
    } else if (!open) {
      reset({
        name: '',
        idCountry: 0,
        rate: 0,
        amount: 0,
        appliesTo: AppliesTo.ALL,
      });
    }
  }, [open, taxRate, reset]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: CreateTaxRateCommand) => {
      return await api.invoiceApi.apiVversionTaxRatePost('1', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxRates'] });
      toast.success({ body: intl.get('taxManagement.createSuccess') });
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      toast.error({
        header: intl.get('taxManagement.createError'),
        body: error.message,
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: UpdateTaxRateCommand) => {
      if (!taxRate?.id) throw new Error('Tax rate ID is required');
      return await api.invoiceApi.apiVversionTaxRateIdPut(taxRate.id, '1', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxRates'] });
      toast.success({ body: intl.get('taxManagement.updateSuccess') });
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      toast.error({
        header: intl.get('taxManagement.updateError'),
        body: error.message,
      });
    },
  });

  const onSubmit = (data: TaxFormData) => {
    if (isEditMode) {
      updateMutation.mutate(data as UpdateTaxRateCommand);
    } else {
      createMutation.mutate(data as CreateTaxRateCommand);
    }
  };

  const countries = countriesData?.data?.queryResult || [];

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 500, p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          {isEditMode
            ? intl.get('taxManagement.edit')
            : intl.get('taxManagement.create')}
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            <Controller
              name="name"
              control={control}
              rules={{ required: intl.get('taxManagement.nameRequired') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={intl.get('taxManagement.name')}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  required
                />
              )}
            />

            <Controller
              name="idCountry"
              control={control}
              rules={{ 
                required: intl.get('taxManagement.countryRequired'),
                validate: (value) => value > 0 || intl.get('taxManagement.countryRequired')
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label={intl.get('taxManagement.country')}
                  error={!!errors.idCountry}
                  helperText={errors.idCountry?.message}
                  fullWidth
                  required
                >
                  <MenuItem value={0} disabled>
                    {intl.get('taxManagement.selectCountry')}
                  </MenuItem>
                  {countries.map((country: any) => (
                    <MenuItem key={country.id} value={country.id}>
                      {intl.get(country.label)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="rate"
              control={control}
              rules={{
                min: { value: 0, message: intl.get('taxManagement.rateMin') },
                max: { value: 100, message: intl.get('taxManagement.rateMax') },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label={intl.get('taxManagement.rate')}
                  error={!!errors.rate}
                  helperText={errors.rate?.message}
                  fullWidth
                  slotProps={{
                    htmlInput: { min: 0, max: 100, step: 0.01 }
                  }}
                />
              )}
            />

            <Controller
              name="amount"
              control={control}
              rules={{
                min: { value: 0, message: intl.get('taxManagement.amountMin') },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label={intl.get('taxManagement.amount')}
                  error={!!errors.amount}
                  helperText={errors.amount?.message}
                  fullWidth
                  slotProps={{
                    htmlInput: { min: 0, step: 0.01 }
                  }}
                />
              )}
            />

            <Controller
              name="appliesTo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label={intl.get('taxManagement.appliesTo')}
                  fullWidth
                >
                  <MenuItem value={AppliesTo.ALL}>
                    {intl.get('taxManagement.appliesToAll')}
                  </MenuItem>
                  <MenuItem value={AppliesTo.ITEM}>
                    {intl.get('taxManagement.appliesToItem')}
                  </MenuItem>
                  <MenuItem value={AppliesTo.INVOICE}>
                    {intl.get('taxManagement.appliesToInvoice')}
                  </MenuItem>
                </TextField>
              )}
            />

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {isEditMode
                  ? intl.get('taxManagement.update')
                  : intl.get('taxManagement.create')}
              </Button>
              <Button
                type="button"
                variant="outlined"
                fullWidth
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose();
                }}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {intl.get('taxManagement.cancel')}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Box>
    </Drawer>
  );
};

export default TaxFormDrawer;
